"use client";
import * as React from "react";
import RouterLink from "next/link";
import { useRouter } from "next/navigation";
import Alert from "@mui/material/Alert";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useState } from "react";
import { useLoginUserMutation } from "@/redux/api/login";
import { setToken } from "@/redux/authSlice";
import { FormHelperText, Link } from "@mui/material";
import { appInitializationReset } from "@/redux/appSlice";
import LoadingButton from "@mui/lab/LoadingButton";

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string().required("Password is required"),
});

const SignInForm = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const [loginUser, { isLoading }] = useLoginUserMutation();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [apiResponse, setApiResponse] = useState<string | null>(null);

  const onSubmit = async (
    values: any,
    { setSubmitting, setFieldError }: any
  ) => {
    setSubmitting(true);
    setApiResponse("loading");

    const result: any = await loginUser(values);

    if ("error" in result) {
      setFieldError("general", result?.error?.message);
      setApiResponse(result.error.data.detail);
      setSubmitting(false);
      return;
    }

    const tokenValue = String(result.data.token);
    dispatch(appInitializationReset());
    dispatch(setToken(tokenValue));
  };

  return (
    <Stack
      spacing={4}
      sx={{
        width: "100%",
        maxWidth: 800,
        margin: "0 auto",
        padding: { xs: 2, sm: "40px" },
      }}
    >
      <Stack spacing={1}>
        <Typography variant="h4">Sign in</Typography>
        <Typography color="text.secondary" variant="body2">
          Don&apos;t have an account?{" "}
          <Link
            component={RouterLink}
            href={"/register"}
            underline="hover"
            variant="subtitle2"
          >
            Sign up
          </Link>
        </Typography>
      </Stack>
      <Formik
        initialValues={{ email: "", password: "" }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, errors, touched }: any) => (
          <Form>
            <Stack spacing={2}>
              <FormControl error={Boolean(touched.email && errors.email)}>
                <InputLabel>Email address</InputLabel>
                <Field
                  as={OutlinedInput}
                  name="email"
                  type="email"
                  label="Email address"
                />
                <FormHelperText>
                  <ErrorMessage name="email" />
                </FormHelperText>
              </FormControl>
              <FormControl error={Boolean(touched.password && errors.password)}>
                <InputLabel>Password</InputLabel>
                <Field
                  as={OutlinedInput}
                  name="password"
                  type={showPassword ? "text" : "password"}
                  label="Password"
                  endAdornment={
                    <Typography
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ cursor: "pointer" }}
                    >
                      {showPassword ? "Hide" : "Show"}
                    </Typography>
                  }
                />
                <FormHelperText>
                  <ErrorMessage name="password" />
                </FormHelperText>
              </FormControl>
              <div>
                <Link
                  component={RouterLink}
                  href="/forgot-password"
                  variant="subtitle2"
                >
                  Forgot password?
                </Link>
              </div>
              {apiResponse && apiResponse !== "loading" && (
                <Alert color="error">{apiResponse}</Alert>
              )}
              <LoadingButton
                disabled={isSubmitting || isLoading}
                type="submit"
                loading={isLoading}
                variant="contained"
                sx={{ backgroundColor: "#635BFF", fontSize: "17px" }}
              >
                Submit
              </LoadingButton>
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default SignInForm;
